import { useEffect, useMemo, useState } from 'react'
import { useAllIntegrations } from '../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../utils/api-client/api-schemas.ts'
import {
  convertIntegrationTypeToTool,
  getIntegrationTypeName,
  isToolIntegration,
  type ToolIntegration,
} from '../../utils/repository-utils.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import { SearchableCombobox } from './searchable-combobox.tsx'
import { SelectBox } from './select-box.tsx'

export type ToolIntegrationType = ToolIntegration['type']

export type IntegrationInstanceSelection = {
  type?: ToolIntegrationType
  integrationId?: string
}

export type IntegrationInstanceSelectorProps = {
  value: IntegrationInstanceSelection
  onChange: (value: IntegrationInstanceSelection) => unknown
  /** Fixes the tool, hiding the tool control — for surfaces already scoped to one tool. */
  fixedType?: ToolIntegrationType
  /** Restricts which tools the tool control offers. Defaults to every tool with an instance. */
  typeFilter?: readonly ToolIntegrationType[]
  toolLabel?: string
  instanceLabel?: string
  /**
   * Marks the instance field required. Set by surfaces that refuse to persist an unattributed
   * record; the surface still owns the submit gate, this only makes the requirement visible.
   */
  isInstanceRequired?: boolean
  /** Shown as the instance field's error when the selected tool has no instances connected. */
  emptyStateText?: string
}

/**
 * Instances of the same tool share an icon and often a name too, so the host is what tells them
 * apart. It rides as option metadata rather than in the label, which keeps it out of the value the
 * trigger displays and out of the text the search highlights.
 */
const getInstanceHost = (integration: ToolIntegration): string | undefined => {
  const baseUri = integration.base_uri
  if (!baseUri) {
    return undefined
  }
  try {
    return new URL(baseUri).host
  } catch {
    return baseUri
  }
}

type InstanceOption = { id: string; label: string; metadata?: string; integration: ToolIntegration }

/** A tool integration the picker can offer: `id` is optional on the wire but required to select one. */
type SelectableInstance = ToolIntegration & { id: string }

/**
 * The instances of a tool a user can actually choose. Exported because a surface that gates
 * submission on "has this tool got an instance?" must ask the same question the picker answers —
 * deriving it separately let the two disagree, disabling submit over an instance the picker had
 * already filtered out (`id` is optional on the wire, and only identified instances are selectable).
 */
export const getSelectableInstances = (
  integrations: Integration[],
  type: ToolIntegrationType | undefined
): SelectableInstance[] =>
  integrations
    .filter(isToolIntegration)
    .filter(
      (integration): integration is SelectableInstance => integration.type === type && integration.id !== undefined
    )

const buildInstanceOptions = (integrations: SelectableInstance[]): InstanceOption[] => {
  const labelCounts = new Map<string, number>()
  // A system singleton is often named after its tool, and "Datadog — Datadog" reads as a bug.
  // The integration-name table, not the tool-name one: they agree everywhere except Polaris, which
  // the tool table calls "Black Duck Polaris SAST" — a third name, in a list of one-word siblings.
  const baseLabelFor = (integration: ToolIntegration) => {
    const toolName = getIntegrationTypeName(integration.type)
    return integration.name && integration.name !== toolName ? `${toolName} — ${integration.name}` : toolName
  }
  for (const integration of integrations) {
    const base = baseLabelFor(integration)
    labelCounts.set(base, (labelCounts.get(base) ?? 0) + 1)
  }

  return integrations.map(integration => {
    const base = baseLabelFor(integration)
    const host = getInstanceHost(integration)
    const isAmbiguous = (labelCounts.get(base) ?? 0) > 1 && host !== undefined
    return { id: integration.id, label: base, metadata: isAmbiguous ? host : undefined, integration }
  })
}

/**
 * Searches the label and the metadata together. The host lives in the metadata precisely because it
 * is what tells same-named instances apart, so it has to be searchable — matching the label alone
 * would leave the ambiguous case, the one search exists for, unsearchable.
 */
const matchesInstanceQuery = (option: InstanceOption, query: string | undefined): boolean => {
  if (!query) {
    return true
  }
  return `${option.label} ${option.metadata ?? ''}`.toLowerCase().includes(query.toLowerCase())
}

export const IntegrationInstanceSelector: React.FC<IntegrationInstanceSelectorProps> = ({
  value,
  onChange,
  fixedType,
  typeFilter,
  toolLabel = 'Tool',
  instanceLabel = 'Instance',
  isInstanceRequired = false,
  emptyStateText = 'No instances of this tool are connected yet. Connect one in Organization → Integrations.',
}) => {
  const selectedType = fixedType ?? value.type
  // Undefined means "showing the selected instance's label" rather than a query the user typed.
  const [instanceQuery, setInstanceQuery] = useState<string | undefined>(undefined)

  // Every tool instance is fetched so the tool control only offers tools that actually have one; the
  // instance list is then narrowed client-side, which keeps switching tools instant.
  const { integrations, isLoading } = useAllIntegrations()

  const toolIntegrations = useMemo(() => integrations.filter(isToolIntegration), [integrations])

  const availableTypes = useMemo(() => {
    const types = new Set(toolIntegrations.map(integration => integration.type))
    return [...types].filter(type => !typeFilter || typeFilter.includes(type)).sort()
  }, [toolIntegrations, typeFilter])

  const instancesForType = useMemo(
    () => getSelectableInstances(toolIntegrations, selectedType),
    [toolIntegrations, selectedType]
  )

  const instanceOptions = useMemo(() => buildInstanceOptions(instancesForType), [instancesForType])

  const selectedLabel = instanceOptions.find(option => option.id === value.integrationId)?.label

  // Default-to-one: a tool with a single instance needs no choice, so select it up front.
  useEffect(() => {
    if (!selectedType || value.integrationId !== undefined || instancesForType.length !== 1) {
      return
    }
    const [only] = instancesForType
    if (only?.id) {
      onChange({ type: selectedType, integrationId: only.id })
    }
  }, [selectedType, value.integrationId, instancesForType, onChange])

  const handleTypeChange = (nextType: string) => {
    const type = availableTypes.find(candidate => candidate === nextType)
    setInstanceQuery(undefined)
    onChange({ type, integrationId: undefined })
  }

  /**
   * The combobox writes the chosen option's value — an instance id — into its field. Selecting is
   * therefore an id arriving here, which we report upwards and then display as that instance's
   * label; anything else is free-typed search text.
   */
  const handleInstanceSearchChange = (nextValue: string) => {
    const matched = instanceOptions.find(option => option.id === nextValue)
    if (matched) {
      setInstanceQuery(undefined)
      onChange({ type: selectedType, integrationId: matched.id })
      return
    }
    setInstanceQuery(nextValue)
  }

  // Keyed off `isLoading`, not `isFetching`: the latter goes true again on every background refetch
  // and would blink the error away and back.
  const hasNoInstances = selectedType !== undefined && !isLoading && instancesForType.length === 0
  // One instance is not a choice: it is already selected above, so the field states it and stops
  // behaving like a control. Mirrors the Integration field in the New repository modal.
  const hasOnlyOneInstance = instanceOptions.length === 1

  return (
    <>
      {fixedType === undefined && (
        <SelectBox
          label={toolLabel}
          placeholder="Select a tool"
          value={value.type}
          onValueChange={handleTypeChange}
          options={availableTypes.map(type => ({
            value: type,
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ToolIcon tool={convertIntegrationTypeToTool(type)} size="small" />
                {getIntegrationTypeName(type)}
              </span>
            ),
          }))}
        />
      )}
      {selectedType !== undefined && (
        <SearchableCombobox
          label={instanceLabel}
          isRequired={isInstanceRequired}
          placeholder="Search instances..."
          // Ariakit treats an undefined value as uncontrolled and keeps its own last value, which
          // would strand the previous tool's instance in the field after switching tools.
          searchValue={instanceQuery ?? selectedLabel ?? ''}
          setSearchValue={handleInstanceSearchChange}
          isFetching={isLoading}
          // This is a typeahead, so clearing the query is useful; the control only shows while the
          // field has focus, so a settled selection is not left wearing a ✕.
          isDisabled={hasOnlyOneInstance}
          errorText={hasNoInstances ? emptyStateText : undefined}
          options={instanceOptions
            .filter(option => matchesInstanceQuery(option, instanceQuery))
            .map(option => ({
              value: option.id,
              label: option.label,
              metadata: option.metadata,
              checked: option.id === value.integrationId,
              icon: <ToolIcon tool={convertIntegrationTypeToTool(option.integration.type)} size="medium" />,
            }))}
        />
      )}
    </>
  )
}
